import { ReactElement } from 'react';
import { pdf, DocumentProps } from '@react-pdf/renderer';

// Utils
import { showErrorToast } from '@/utils/errorHandlers';

// Constants
import { ERROR_MESSAGES, SOCIAL_DOMAINS } from '@/constants';

export const downloadPDF = async (
  CV: ReactElement<DocumentProps>,
  fileName: string,
) => {
  let url = '';
  try {
    const blob = await pdf(CV).toBlob();
    url = URL.createObjectURL(blob);

    const response = await fetch(url);
    const blobData = await response.blob();
    const blobUrl = window.URL.createObjectURL(blobData);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.click();
  } catch (_) {
    showErrorToast(ERROR_MESSAGES.DOWNLOAD_FILE_FAILED);
  } finally {
    if (url) URL.revokeObjectURL(url);
  }
};

/**
 * Extracts the identifier from a social media URL
 * @param url - The URL to extract the identifier from
 * @returns The identifier from the URL
 * @example
 * getSocialMediaIdentifier('https://www.github.com/john-doe') // 'john-doe'
 * getSocialMediaIdentifier('https://www.threads.net/@john-doe') // 'john-doe'
 * getSocialMediaIdentifier('https://www.tiktok.com/@john-doe') // 'john-doe'
 * getSocialMediaIdentifier('https://www.linkedin.com/in/john-doe') // 'john-doe'
 * getSocialMediaIdentifier('https://www.facebook.com/john-doe') // 'john-doe'
 * getSocialMediaIdentifier('https://www.facebook.com/profile.php?id=123') // '123'
 * getSocialMediaIdentifier('https://www.instagram.com/john-doe') // 'john-doe'
 * getSocialMediaIdentifier('https://www.3.instagram.com/john-doe') // 'john-doe'
 */
export const getSocialMediaIdentifier = (url: string): string => {
  if (!url || typeof url !== 'string') return '';

  try {
    const urlObj = new URL(url);

    const hostnameParts = urlObj.hostname.toLowerCase().split('.');
    const hostname = hostnameParts.slice(-2).join('.');

    const pathnameParts = urlObj.pathname?.split('/').filter(Boolean) ?? [];

    const normalizePath = (path: string): string =>
      (path || '').replace(/^\/+/, '').replace(/^@/, '');

    // Facebook profile.php special case
    if (
      hostname === SOCIAL_DOMAINS.FACEBOOK &&
      /profile\.php/.test(urlObj.pathname ?? '')
    ) {
      return new URLSearchParams(urlObj.search ?? '').get('id') ?? hostname;
    }

    let result = '';

    switch (hostname) {
      case SOCIAL_DOMAINS.GITHUB:
        result = pathnameParts[0] ?? '';
        break;

      case SOCIAL_DOMAINS.LINKEDIN:
        result = pathnameParts[1] ?? '';
        break;

      case SOCIAL_DOMAINS.THREADS:
      case SOCIAL_DOMAINS.INSTAGRAM:
        result = normalizePath(pathnameParts.join('/')).split(/[/?#]/)[0] ?? '';
        break;

      case SOCIAL_DOMAINS.TIKTOK:
      case SOCIAL_DOMAINS.FACEBOOK:
        result = normalizePath(pathnameParts.join('/')) ?? '';
        break;

      default:
        return hostname.split('.')[0] ?? '';
    }

    // Use the hostname as identifier if no specific identifier was found
    return (result || hostname.split('.')[0]) ?? '';
  } catch {
    return '';
  }
};
